// Site Hub: one site's program, people and records on one page. #/sites/{n}
// Words: COHESION_WORDS.md, Move 1. Mockup: MOCKUP_SITE_HUB.html rev 1 (approved 2026-10-23, 7:29 PM).
//
// Rules:
// * The steps run in Drive site folder order, 00 to 08 (drive-folders.ts).
// * Each step reads Not Started, In Progress or Done (Home rev 3, 2026-10-23: no dots).
//   The model keeps gray, yellow, green underneath; the screens show the words.
// * Red is overduty only. It shows as a count on 05 and 06, never as a step state.
// * Adds no data and no new jobs. Every count is worked out the way its own page works it out.
// Pure: data in, view out.

import type { AfStudySummary, FieldWalkDoc } from "./firebase";
import type { FieldSite } from "./field-labels-site";
import type { RosterRow } from "./gloves-roster";
import type { NearMissRow } from "./near-miss";
import type { PackAsset, PackLink } from "./survey";
import { tracedSet } from "./survey";
import { hasPlantManager, siteContacts, type Contact, type Site } from "./sites";
import { currentStudyState, isManualStep, markLine, marksOf, stageLights, stripOfLights, tagClaimed, type Light, type StageFacts } from "./site-stage";
import type { VisitRow } from "./contractor";
import { visitNeedsLook } from "./contractor";
import type { WorkRequestRow } from "./work-requests";

export type { Light } from "./site-stage";

export interface HubStep {
  n: string;
  name: string;
  /** The one grey line under the name. */
  fact: string;
  light: Light;
  /** Where the row goes. Blank on a step that is not built yet. */
  href: string;
  /** DANGER labels, the overduty count. 0 shows nothing. */
  red: number;
  /** Not built yet: grey, not a link. */
  off: boolean;
  /** 00 to 04: Done is the admin's check on the hub (COHESION_CHANGE_LOG.md change 2). */
  manual?: boolean;
  /** "Marked Done · Robert Stuart · Today 6:12 AM", blank when not marked. */
  markedLine?: string;
}

/** One site's program data, read from the cloud when the hub opens. */
export interface ProgramData {
  /** Every asset record as stored, removed ones included. */
  assetDocs: Record<string, Record<string, unknown>>;
  assets: PackAsset[];
  links: PackLink[];
  /** Asset IDs with an IEA record. Null when the badge has no IEA Site Survey switch. */
  ieaDone: string[] | null;
  studies: AfStudySummary[];
  /** The Field Labels walk the cloud holds (rule 36). Null when none was sent yet. */
  fieldWalk?: FieldWalkDoc | null;
}

export interface HubInput {
  site: Site & { driveFolder?: { id?: string; url?: string; rootFolderId?: string } | null };
  contacts: Contact[];
  /** Null while it loads. A string when it could not be read. */
  program: ProgramData | null | string;
  /** The Field Labels walk saved on this device, and when. Null when there is none here. */
  fieldLabels: FieldSite | null;
  fieldLabelsSavedAt?: string;
  device: { packSavedAt: string | null; waiting: number | null };
  /** Drive's root folder, once Program Settings has connected it. */
  driveRoot: string | null;
  badges: { "Badge ID": string; Status?: string }[] | null;
  roster: RosterRow[] | null;
  workRequests: WorkRequestRow[] | null;
  nearMisses: NearMissRow[] | null;
  visits: VisitRow[] | null;
  nowIso: string;
}

const n = (count: number, one: string, many = `${one}s`) => `${count.toLocaleString("en-US")} ${count === 1 ? one : many}`;
const LOADING = "…";

/** The live asset records: not removed. */
function liveDocs(p: ProgramData): [string, Record<string, unknown>][] {
  return Object.entries(p.assetDocs).filter(([, d]) => d.isRemoved !== true);
}

/** Same rule as the Asset ID Creator's Print QR Labels count (asset-id-page.ts notPrinted). */
export function notPrintedCount(p: ProgramData): number {
  return liveDocs(p).filter(([, d]) => d.source === "Asset ID page" && !d.qrLabelPrintedAt).length;
}

/** The study the site works from: the newest one not replaced. */
export function currentStudy(studies: readonly AfStudySummary[]): AfStudySummary | null {
  return studies.find((s) => s.state !== "Replaced") ?? null;
}

const QEW_TAG = (site: string) => new RegExp(`^${site}-QEW\\d{2}$`);

/**
 * Step 00 reads the newer of the walk on this device and the walk in the cloud, so it is right on any device.
 * Counts only: rooms done of rooms, and whether any gear is in.
 */
export function fieldLabelsStep(site: string, f: FieldSite | null, localAt = "", cloud: FieldWalkDoc | null = null): HubStep {
  const base = { n: "00", name: "Field Labels", href: `#/assets/${site}/labels`, red: 0, off: false };
  const local = f && f.rooms.length ? { total: f.rooms.length, done: f.rooms.filter((r) => r.done).length, gear: f.rooms.some((r) => r.gear.length), at: localAt } : null;
  const fromCloud = cloud && cloud.roomsTotal ? { total: cloud.roomsTotal, done: cloud.roomsDone, gear: cloud.gearTotal > 0, at: cloud.savedAt } : null;
  const use = local && fromCloud ? (local.at >= fromCloud.at ? local : fromCloud) : (local ?? fromCloud);
  if (!use) return { ...base, fact: "No rooms yet", light: "gray" };
  // Started once the walk has a room. Done is the admin's check (change 2).
  return { ...base, fact: `${use.done} of ${n(use.total, "room")} done`, light: "yellow" };
}

export function hubSteps(i: HubInput): HubStep[] {
  const s = i.site.siteNumber;
  const p = typeof i.program === "object" ? i.program : null;
  const wait = typeof i.program === "string" ? "Not loaded" : LOADING;
  const steps: HubStep[] = [fieldLabelsStep(s, i.fieldLabels, i.fieldLabelsSavedAt ?? "", p?.fieldWalk ?? null)];

  // 01 Asset IDs
  {
    const live = p ? liveDocs(p).length : 0;
    const waiting = i.device.waiting ?? 0;
    const fact = !p ? wait : !live ? "No assets yet" : waiting ? `${n(live, "asset")} · ${waiting} waiting to send` : n(live, "asset");
    steps.push({ n: "01", name: "Assessment", href: `#/assets/${s}`, red: 0, off: false, fact, light: "gray" });
  }

  // 02 QR Labels
  {
    const live = p ? liveDocs(p).length : 0;
    const left = p ? notPrintedCount(p) : 0;
    const fact = !p ? wait : !live ? "No assets yet" : left ? `${n(left, "label")} to print` : "All printed";
    steps.push({ n: "02", name: "QR Labels", href: `#/assets/${s}`, red: 0, off: false, fact, light: "gray" });
  }

  // 03 QEW Tags
  {
    const tags = i.badges ? i.badges.filter((b) => QEW_TAG(s).test(b["Badge ID"])) : null;
    const active = tags ? tags.filter((b) => b.Status === "Active").length : 0;
    const fact = !tags ? LOADING : !tags.length ? "No tags yet" : `${n(active, "Active QEW")} · ${n(tags.length, "tag")}`;
    // This site's tags, not every site's (change 5).
    steps.push({ n: "03", name: "QEW Tags", href: `#/people/tags/${s}`, red: 0, off: false, fact, light: "gray" });
  }

  // 04 Survey: the same counts as the survey page (survey.ts).
  {
    let fact = wait;
    if (p) {
      const live = p.assets.filter((a) => !a.isOutOfService);
      if (!live.length) fact = "No assets yet";
      else if (p.ieaDone === null) fact = "Needs the IEA Site Survey switch";
      else {
        const traced = tracedSet(p);
        const done = new Set(p.ieaDone);
        const mapped = live.filter((a) => traced.has(a.assetId)).length;
        const collected = live.filter((a) => done.has(a.assetId)).length;
        fact = `${mapped} of ${live.length} mapped · ${collected} collected`;
      }
    }
    steps.push({ n: "04", name: "Survey", href: `#/survey/${s}`, red: 0, off: false, fact, light: "gray" });
  }

  // 05 Arc Flash Study and 06 Arc Flash Labels
  {
    const st = p ? currentStudy(p.studies) : null;
    const danger = st ? st.counts.danger : 0;
    const studyHref = st ? `#/af-labels/${s}/s/${st.studyId}` : `#/af-labels/${s}/import`;
    const studyFact = !p ? wait : st ? `Assessed ${st.assessmentDate} · ${n(st.counts.labels, "label")}` : "No study yet";
    steps.push({ n: "05", name: "Arc Flash Study", href: studyHref, red: danger, off: false, fact: studyFact, light: "gray" });
    const labelsFact = !p ? wait : !st ? "Waits on the study" : st.state === "Labels Created" ? `${n(st.counts.warning, "WARNING")} · ${danger} DANGER` : "Not created yet";
    steps.push({ n: "06", name: "Arc Flash Labels", href: st ? studyHref : `#/af-labels/${s}`, red: danger, off: false, fact: labelsFact, light: "gray" });
  }

  // 07 Verification: the hang check walk, not built yet.
  steps.push({ n: "07", name: "Verification", href: "", red: 0, off: true, fact: "Coming", light: "gray" });

  // 08 Site Info
  {
    const pm = hasPlantManager(i.contacts, s);
    const folder = !!i.site.driveFolder?.id && (!i.driveRoot || i.site.driveFolder.rootFolderId === i.driveRoot);
    const place = !!(i.site.city && i.site.state);
    const missing = [place ? "" : "No city", pm ? "" : "No Plant Manager", folder ? "" : "No Drive folder"].filter(Boolean);
    const fact = missing.length ? missing.join(" · ") : "Plant Manager set · Drive folder set";
    steps.push({ n: "08", name: "Site Info", href: `#/sites/${s}/edit`, red: 0, off: false, fact, light: "gray" });
  }
  // The lights come from the same rules the server's Refresh uses (site-stage.ts).
  const lights = stageLights(hubFacts(i));
  const marks = marksOf(i.site.stepsDone);
  return steps.map((x, k) => {
    const manual = isManualStep(x.n);
    const m = manual ? marks[x.n as keyof typeof marks] : undefined;
    return { ...x, light: lights[k] ?? "gray", manual, markedLine: m ? markLine(m, whenLine(m.at, i.nowIso)) : "" };
  });
}

/** What the stage is worked out from, as the hub sees it. The server reads the same facts for Refresh. */
export function hubFacts(i: HubInput): StageFacts {
  const s = i.site.siteNumber;
  const p = typeof i.program === "object" ? i.program : null;
  const local = i.fieldLabels ? i.fieldLabels.rooms.length > 0 : false;
  const cloud = !!p?.fieldWalk && p.fieldWalk.roomsTotal > 0;
  const live = p ? liveDocs(p) : [];
  const tags = i.badges ? i.badges.filter((b) => QEW_TAG(s).test(b["Badge ID"])) : [];
  return {
    started: {
      "00": local || cloud,
      "01": live.length > 0,
      "02": live.some(([, d]) => !!d.qrLabelPrintedAt),
      "03": tags.some((b) => tagClaimed(b.Status)),
      "04": !!p && (p.links.length > 0 || (p.ieaDone?.length ?? 0) > 0),
    },
    marks: marksOf(i.site.stepsDone),
    studyState: p ? currentStudyState(p.studies) : null,
    plantManager: hasPlantManager(i.contacts, s),
    city: !!(i.site.city && i.site.state),
    driveFolder: !!i.site.driveFolder?.id && (!i.driveRoot || i.site.driveFolder.rootFolderId === i.driveRoot),
  };
}

/**
 * True when the hub has everything the stage needs, so the stage can be sent to the site record
 * (STAGE_CLOUD_WORDS.md): the program read, the tags read, and the survey records.
 */
export function stageReady(i: HubInput): boolean {
  return typeof i.program === "object" && i.program !== null && i.program.ieaDone !== null && i.badges !== null;
}

/** The Home row strip: one letter per step 00 to 08, g y n. Kept on this device and sent to the site record. */
export function stripOf(steps: readonly HubStep[]): string {
  return stripOfLights(steps.map((x) => x.light));
}

// ---- Stage in words (MOCKUP_HOME_REV3.html, approved 2026-10-23, 9:00 PM) ----
// No dots. Each step reads Not Started, In Progress or Done. Each site reads Not Started,
// In Progress or Complete, with a bar of one block per step and the step it is on now.

/** The word for a step's state on the hub. */
// STEP_STATUS_WORDS.md (approved 2026-10-26): the same three words for a step and a site, light to dark.
export const STEP_WORD: Record<Light, string> = { gray: "Not Started", yellow: "In Process", green: "Complete" };

/** Steps 00 to 08 in strip order. 07 Verification is not built yet and is left out of the stage. */
export const PROGRAM_STEPS: readonly { n: string; name: string; counted: boolean }[] = [
  { n: "00", name: "Field Labels", counted: true },
  { n: "01", name: "Assessment", counted: true },
  { n: "02", name: "QR Labels", counted: true },
  { n: "03", name: "QEW Tags", counted: true },
  { n: "04", name: "Survey", counted: true },
  { n: "05", name: "Arc Flash Study", counted: true },
  { n: "06", name: "Arc Flash Labels", counted: true },
  { n: "07", name: "Verification", counted: false },
  { n: "08", name: "Site Info", counted: true },
];

export type StageKind = "ns" | "ip" | "dn";

export interface SiteStage {
  kind: StageKind;
  /** NOT STARTED, IN PROCESS, COMPLETE. */
  word: string;
  done: number;
  total: number;
  /** One block per counted step: d done, w in progress, n not started. */
  blocks: string;
  /** "05 Arc Flash Study", the first counted step not done. Blank when complete. */
  now: string;
}

/** A site's stage from its strip. Null when the strip is missing or not the nine steps. */
export function stageOfStrip(strip: string | undefined | null): SiteStage | null {
  if (!strip || strip.length !== PROGRAM_STEPS.length || /[^gyn]/.test(strip)) return null;
  const counted = PROGRAM_STEPS.map((s, i) => ({ ...s, c: strip[i]! })).filter((s) => s.counted);
  const done = counted.filter((s) => s.c === "g").length;
  const total = counted.length;
  const kind: StageKind = done === total ? "dn" : counted.every((s) => s.c === "n") ? "ns" : "ip";
  const word = kind === "dn" ? "COMPLETE" : kind === "ns" ? "NOT STARTED" : "IN PROCESS";
  const next = counted.find((s) => s.c !== "g");
  return {
    kind,
    word,
    done,
    total,
    blocks: counted.map((s) => (s.c === "g" ? "d" : s.c === "y" ? "w" : "n")).join(""),
    now: next ? `${next.n} ${next.name}` : "",
  };
}

export interface HereRow {
  name: string;
  line: string;
  href: string;
  /** Amber count. 0 shows a plain number or nothing. */
  alert: number;
  count: number | null;
}

export function peopleHere(i: HubInput): HereRow[] {
  const s = i.site.siteNumber;
  const tags = i.badges ? i.badges.filter((b) => QEW_TAG(s).test(b["Badge ID"])) : null;
  const active = tags ? tags.filter((b) => b.Status === "Active").length : null;
  const roster = i.roster ? i.roster.filter((r) => r.siteNumber === s) : null;
  const soon = roster ? roster.filter((r) => r.bucket === "soon").length : 0;
  const expired = roster ? roster.filter((r) => r.bucket === "expired").length : 0;
  return [
    { name: "Active QEWs", line: tags ? n(tags.length, "tag") : LOADING, href: `#/people/site/${s}`, alert: 0, count: active },
    {
      name: "Gloves",
      line: !roster ? LOADING : soon || expired ? [soon ? `${soon} due inside 30 days` : "", expired ? `${expired} expired` : ""].filter(Boolean).join(" · ") : "All current",
      href: `#/gloves/${s}`,
      alert: soon,
      count: roster ? roster.length : null,
    },
    { name: "Training", line: "Class and trainees", href: `#/training/${s}`, alert: 0, count: null },
  ];
}

export function recordsHere(i: HubInput): HereRow[] {
  const s = i.site.siteNumber;
  const wr = i.workRequests ? i.workRequests.filter((r) => r.siteNumber === s && r.status === "Open").length : null;
  const nm = i.nearMisses ? i.nearMisses.filter((r) => r.siteNumber === s && r.status === "Open").length : null;
  const cv = i.visits ? i.visits.filter((v) => v.siteNumber === s && visitNeedsLook(v, i.nowIso)).length : null;
  const row = (name: string, count: number | null, href: string, none: string, some: string): HereRow => ({
    name,
    line: count === null ? LOADING : count ? some : none,
    href,
    alert: count ?? 0,
    count,
  });
  return [
    row("Work Requests", wr, `#/work-requests/${s}`, "None open", "Open"),
    row("Near Miss", nm, `#/near-miss/${s}`, "None open", "Open"),
    row("Contractor Visits", cv, `#/contractors/${s}`, "None waiting", "Waiting on you"),
  ];
}

export interface DeviceCard {
  packLine: string;
  ready: boolean;
}

export function deviceCard(d: HubInput["device"]): DeviceCard {
  const pack = d.packSavedAt ? "Site pack saved" : "No site pack";
  const out = d.waiting === null ? "" : d.waiting ? ` · ${d.waiting} waiting in the Outbox` : " · Outbox empty";
  return { packLine: pack + out, ready: !!d.packSavedAt && !d.waiting };
}

/** Contacts shown on the hub: the site's, active first, as the old site page listed them. */
export function hubContacts(i: HubInput): Contact[] {
  return siteContacts(i.contacts, i.site.siteNumber);
}

// ---- Continue: the last step worked at each site, kept on this device ----

export interface LastStep {
  step: string;
  name: string;
  href: string;
  at: string;
}

/** Which step a page is, from its address. Null for pages that are not a step. */
export function stepOfRoute(parts: readonly string[]): { site: string; step: string; name: string } | null {
  const [top, site, sub] = parts;
  if (!site || !/^\d{2}$/.test(site)) return null;
  if (top === "assets") {
    if (sub === "labels") return { site, step: "00", name: "Field Labels" };
    if (!sub || sub === "outbox") return { site, step: "01", name: "Assessment" };
    return null;
  }
  if (top === "survey" && (sub === "map" || sub === "walk" || sub === "a")) return { site, step: "04", name: "Survey" };
  if (top === "af-labels") return { site, step: "06", name: "Arc Flash Labels" };
  return null;
}

export const lastStepKey = (company: string): string => `safetag.hub.lastStep.v1.${company}`;
export const stripKey = (company: string): string => `safetag.hub.strip.v1.${company}`;

export function readJsonMap<T>(raw: string | null): Record<string, T> {
  try {
    const v = raw ? (JSON.parse(raw) as unknown) : null;
    return v && typeof v === "object" ? (v as Record<string, T>) : {};
  } catch {
    return {};
  }
}

/** "Yesterday 4:12 PM", "Today 9:05 AM", "Oct 20". */
export function whenLine(iso: string, nowIso: string): string {
  const d = new Date(iso);
  const now = new Date(nowIso);
  if (Number.isNaN(d.getTime())) return "";
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86400000);
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (diff === 0) return `Today ${time}`;
  if (diff === 1) return `Yesterday ${time}`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
